import { Component, Input, OnInit } from "@angular/core";

@Component({
    selector: "flag",
    templateUrl: "./flag.component.html",
    styleUrls: ["./flag.component.scss"],
    standalone: false
})
export class FlagComponent implements OnInit {
  /**
   * Country code for the flag, e.g., 'us' for the United States.
   * Specify this input when you know the exact country code to display the appropriate flag.
   * @example <flag countryCode="us"></flag> // Displays the U.S. flag
   */
  @Input() countryCode: string;

  /**
   * Locale ID used as a fallback to determine the country code if not directly provided.
   * This input is useful for setting flags based on language or user location.
   * @example <flag localeId="1"></flag> // Displays the flag for the Czech Republic (localeId = 1)
   */
  @Input() localeId: number;

  /**
   * The style type of the flag, which can be 'wide', 'round', or 'square'.
   * - 'wide': Displays a default rectangular flag
   * - 'round': Displays a circular flag
   * - 'square': Displays a square flag
   * Set this input to modify the flag's appearance.
   * @example <flag countryCode="gb" type="round"></flag> // Displays the UK flag in a round shape
   */
  @Input() type: 'wide' | 'round' | 'square' = 'wide';

  /**
   * Additional CSS classes for custom styling of the flag component.
   * This can be used to further customize the component's appearance.
   * @example <flag countryCode="de" class="custom-border"></flag> // Adds custom-border class to the German flag
   */
  @Input() class: string = '';

  /**
   * The complete CSS class string for the flag element.
   * This string is constructed based on the type, country code, and any additional classes.
   */
  completeClass: string = 'fi';

  constructor() {}

  /**
   * Initializes the component by setting the completeClass property based on the flag type and any additional classes.
   * - Adds specific styles for round and square types.
   * - Combines the base flag class with country-specific and custom classes.
   */
  ngOnInit(): void {
    // Set style based on flag type
    switch (this.type) {
      case 'round':
        // Adds rounded class for circular flag
        this.completeClass += ' rounded-full fis';
        break;
      case 'square':
        // Adds square class for square flag
        this.completeClass += ' fis';
        break;
      default:
        // Default is wide, no additional classes required
        break;
    }

    // Add country-specific and custom classes
    this.completeClass += ' ' + this.getFlagClass();
    if (this.class) {
      this.completeClass += ' ' + this.class;
    }
  }

  /**
   * Constructs the country-specific flag class string.
   * - If countryCode is provided, uses it directly.
   * - If countryCode is not provided, falls back to localeId.
   * - Sets a default empty string if neither countryCode nor localeId is defined.
   *
   * @returns The CSS class for the flag of the specified country.
   */
  private getFlagClass(): string {
    let countryIconClass = 'fi-';

    // Use country code if available
    if (this.countryCode) {
      countryIconClass += this.countryCode.toLowerCase();
    } else if (this.localeId) {
      // Map locale ID to country code if country code is absent
      switch (this.localeId) {
        case 1:
          this.countryCode = 'cz'; // Czech Republic
          break;
        case 2:
          this.countryCode = 'gb'; // United Kingdom
          break;
        default:
          break;
      }
      countryIconClass += this.countryCode?.toLowerCase();
    } else {
      // Return an empty string if no country information is available
      return '';
    }

    return countryIconClass;
  }
}
